import type { DigitalProduct, TopbarMode } from '@/types/digitalProduct'

/**
 * Aparência do evento, e nada além disso.
 *
 * Tipo próprio e estreito de propósito: NÃO é um `Partial<DigitalProduct>`.
 * Dado velho que pinta é inofensivo; dado velho que decide é defeito de
 * negócio. `start_at`, `end_at` e `status` ficam de fora para que derivar
 * vigência daqui esbarre no compilador.
 */
export interface TenantIdentity {
  color: string | null
  topbar_mode: TopbarMode | null
  topbar_color_light: string | null
  topbar_color_dark: string | null
  topbarTextureUrl: string | null
  logoUrl: string | null
  name: string | null
  description: string | null
  faviconUrl: string | null
  backgroundImageDesktopUrl: string | null
  backgroundImageMobileUrl: string | null
}

/** Ordem canônica, usada pela comparação e pela serialização. */
export const TENANT_IDENTITY_KEYS = [
  'color',
  'topbar_mode',
  'topbar_color_light',
  'topbar_color_dark',
  'topbarTextureUrl',
  'logoUrl',
  'name',
  'description',
  'faviconUrl',
  'backgroundImageDesktopUrl',
  'backgroundImageMobileUrl'
] as const satisfies readonly (keyof TenantIdentity)[]

export const EMPTY_TENANT_IDENTITY: TenantIdentity = {
  color: null,
  topbar_mode: null,
  topbar_color_light: null,
  topbar_color_dark: null,
  topbarTextureUrl: null,
  logoUrl: null,
  name: null,
  description: null,
  faviconUrl: null,
  backgroundImageDesktopUrl: null,
  backgroundImageMobileUrl: null
}

type MediaLike = { file?: { url?: string | null } | null } | null | undefined

function mediaUrl(media: MediaLike): string | null {
  return media?.file?.url ?? null
}

function orNull<T>(value: T | undefined): T | null {
  return value ?? null
}

/**
 * Aceita `Partial` porque a resposta da query pode chegar incompleta durante o
 * boot — projetar o que existe é melhor que exigir o objeto inteiro.
 */
export function toTenantIdentity(
  digitalProduct: Partial<DigitalProduct> | null | undefined
): TenantIdentity {
  if (!digitalProduct) return { ...EMPTY_TENANT_IDENTITY }

  return {
    color: orNull(digitalProduct.color),
    topbar_mode: orNull(digitalProduct.topbar_mode),
    topbar_color_light: orNull(digitalProduct.topbar_color_light),
    topbar_color_dark: orNull(digitalProduct.topbar_color_dark),
    topbarTextureUrl: mediaUrl(digitalProduct.topbar_texture),
    logoUrl: mediaUrl(digitalProduct.logo),
    name: orNull(digitalProduct.name),
    description: orNull(digitalProduct.description),
    faviconUrl: mediaUrl(digitalProduct.favicon),
    backgroundImageDesktopUrl: mediaUrl(
      digitalProduct.background_image_desktop
    ),
    backgroundImageMobileUrl: mediaUrl(digitalProduct.background_image_mobile)
  }
}

/**
 * Compara por conteúdo, nunca por `updated_at`: o carimbo muda por edições que
 * não afetam nada do que o snapshot pinta.
 */
export function sameTenantIdentity(
  a: TenantIdentity | null | undefined,
  b: TenantIdentity | null | undefined
): boolean {
  if (!a || !b) return a === b

  return TENANT_IDENTITY_KEYS.every(key => a[key] === b[key])
}

/**
 * Reduz o que veio do armazenamento às chaves conhecidas. Chave gravada por
 * uma versão futura é descartada em vez de vazar para dentro do app.
 */
export function coerceTenantIdentity(value: unknown): TenantIdentity | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null

  const source = value as Record<string, unknown>
  const identity = { ...EMPTY_TENANT_IDENTITY }
  let hasAnyValue = false

  for (const key of TENANT_IDENTITY_KEYS) {
    const raw = source[key]
    if (typeof raw === 'string' && raw.length > 0) {
      identity[key] = raw as never
      hasAnyValue = true
    }
  }

  return hasAnyValue ? identity : null
}
